import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Bell, Receipt, ArrowLeft, Phone, Info } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, formatPrice, imgUrl, telLink } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Logo } from "@/components/site/Primitives";
import { useSeo } from "@/lib/seo";

const TAGS = ["Ev Yemeği", "Premium", "Usta Tavsiyesi", "7/24", "Acılı", "Vejetaryen"];

function ItemRow({ item, onOpen }) {
  const soldOut = !item.available;
  return (
    <button
      onClick={() => onOpen(item)}
      className={`w-full text-left flex gap-4 py-5 border-b border-[#D4AF37]/10 group ${soldOut ? "opacity-50" : ""}`}
      data-testid={`menu-item-card-${item.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="min-w-0 font-serif-ym text-xl leading-tight font-semibold text-[#FDFBF7] group-hover:text-[#E5C158] transition-colors">{item.name}</h3>
          <span className="flex-1 border-b border-dotted border-[#D4AF37]/25 translate-y-[-4px] hidden sm:block" />
          <span className="font-display text-[#D4AF37] whitespace-nowrap" data-testid={`menu-item-price-${item.id}`}>{formatPrice(item.price)}</span>
        </div>
        {item.description && <p className="mt-1.5 text-sm text-[#CBBBAE] line-clamp-2">{item.description}</p>}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {soldOut && <span className="rounded-full bg-[#C84B31] px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-white">Tükendi</span>}
          {item.tags?.map((t) => (
            <span key={t} className="rounded-full border border-[#D4AF37]/30 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-[#E5C158]">{t}</span>
          ))}
        </div>
      </div>
      {item.image && (
        <div className="h-24 w-24 sm:h-28 sm:w-28 shrink-0 overflow-hidden rounded-md border border-[#D4AF37]/20">
          <img src={imgUrl(item.image)} alt={item.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
        </div>
      )}
    </button>
  );
}

function ItemDialog({ item, onClose }) {
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1F1411] border-[#D4AF37]/30 text-[#FDFBF7] p-0 overflow-hidden max-w-lg" data-testid="menu-item-dialog">
        {item && (
          <>
            {item.image && <img src={imgUrl(item.image)} alt={item.name} className="h-64 w-full object-cover" />}
            <div className="p-6">
              <DialogTitle className="font-serif-ym text-3xl font-semibold">{item.name}</DialogTitle>
              <p className="font-display text-xl text-[#D4AF37] mt-2">{formatPrice(item.price)}</p>
              <DialogDescription className="mt-4 text-[#CBBBAE] leading-relaxed">{item.description}</DialogDescription>
              {(item.allergens || item.calories) && (
                <div className="mt-5 grid grid-cols-2 gap-3 text-xs">
                  {item.calories && <div className="rounded-md border border-[#D4AF37]/20 p-3"><p className="eyebrow !text-[9px]">Kalori</p><p className="mt-1">{item.calories}</p></div>}
                  {item.allergens && <div className="rounded-md border border-[#D4AF37]/20 p-3"><p className="eyebrow !text-[9px]">Alerjenler</p><p className="mt-1">{item.allergens}</p></div>}
                </div>
              )}
              {!item.available && <p className="mt-4 text-sm text-[#C84B31]">Bu ürün şu an tükenmiştir.</p>}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TableBar({ table }) {
  const [sending, setSending] = useState(false);
  const call = async (type) => {
    setSending(true);
    try {
      await api.post("/waiter-calls", { table, type });
      toast.success(type === "hesap" ? "Hesap talebiniz iletildi." : "Garsonumuz hemen geliyor!");
      setTimeout(() => setSending(false), 30000);
    } catch (e) {
      toast.error(errMsg(e));
      setSending(false);
    }
  };
  return (
    <div className="fixed bottom-0 inset-x-0 z-30 bg-[#140D0B]/95 backdrop-blur-xl border-t border-[#D4AF37]/25 p-3" data-testid="menu-table-bar">
      <div className="mx-auto max-w-3xl flex gap-3">
        <button disabled={sending} onClick={() => call("garson")} className="btn-gold flex-1 !py-3" data-testid="menu-call-waiter-btn"><Bell size={16} /> Garson Çağır</button>
        <button disabled={sending} onClick={() => call("hesap")} className="btn-ghost-gold flex-1 !py-3" data-testid="menu-request-bill-btn"><Receipt size={16} /> Hesap İste</button>
      </div>
    </div>
  );
}

export default function MenuPage() {
  const { settings } = useSite();
  const [params] = useSearchParams();
  const table = params.get("table") || params.get("masa");
  const [data, setData] = useState({ categories: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [open, setOpen] = useState(null);

  const jsonLd = useMemo(() => {
    if (!data.categories.length) return null;
    const origin = window.location.origin;
    return {
      "@context": "https://schema.org",
      "@type": "Menu",
      name: "Yeni Mercan Restoran Menü",
      url: `${origin}/menu`,
      inLanguage: "tr-TR",
      hasMenuSection: data.categories.map((c) => ({
        "@type": "MenuSection",
        name: c.name,
        description: c.description,
        hasMenuItem: data.items.filter((i) => i.category_id === c.id).map((i) => ({
          "@type": "MenuItem",
          name: i.name,
          description: i.description,
          ...(i.image ? { image: i.image.startsWith("http") ? i.image : `${origin}${i.image}` } : {}),
          offers: { "@type": "Offer", price: String(i.price), priceCurrency: "TRY", availability: i.available ? "https://schema.org/InStock" : "https://schema.org/OutOfStock" },
        })),
      })),
    };
  }, [data]);

  useSeo({
    title: "Menü & Fiyatlar | Yeni Mercan Restoran Buca – Ev Yemekleri, Kahvaltı, Kokoreç, Izgara",
    description: "Yeni Mercan Restoran Buca güncel menüsü ve KDV dahil fiyatları: serpme kahvaltı, çorbalar, ev yemekleri, premium ızgaralar, kokoreç ve tatlılar. 7/24 açık.",
    path: "/menu",
    noindex: !!table,
    jsonLd,
  });

  useEffect(() => {
    api.get("/menu").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading && window.location.hash) {
      document.getElementById(`cat-${window.location.hash.slice(1)}`)?.scrollIntoView({ behavior: "smooth" });
    }
  }, [loading]);

  const grouped = useMemo(() => {
    const term = q.trim().toLocaleLowerCase("tr");
    return data.categories
      .map((c) => ({
        ...c,
        items: data.items.filter(
          (i) =>
            i.category_id === c.id &&
            (!tag || i.tags?.includes(tag)) &&
            (!term || `${i.name} ${i.description}`.toLocaleLowerCase("tr").includes(term))
        ),
      }))
      .filter((c) => c.items.length);
  }, [data, q, tag]);

  const jump = (slug) => {
    setActiveCat(slug);
    const el = document.getElementById(`cat-${slug}`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 170, behavior: "smooth" });
  };

  return (
    <div className="ym-dark min-h-screen pb-28" data-testid="menu-page">
      <div className="relative h-56 sm:h-72 overflow-hidden">
        <img src="/images/karisik.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#140D0B] to-[#140D0B]/40" />
        <div className="relative mx-auto max-w-3xl px-5 h-full flex flex-col justify-end pb-6">
          <Link to="/" className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-xs text-[#CBBBAE] hover:text-[#D4AF37]" data-testid="menu-back-home-link"><ArrowLeft size={14} /> Ana sayfa</Link>
          <a href={telLink(settings.phone)} className="absolute top-5 right-5 inline-flex items-center gap-1.5 text-xs text-[#CBBBAE] hover:text-[#D4AF37]" data-testid="menu-phone-link"><Phone size={14} /> {settings.phone}</a>
          <div className="flex items-center gap-4">
            <Logo size={72} />
            <div>
              <p className="eyebrow !text-[10px]">Dijital Menü</p>
              <h1 className="font-display text-2xl sm:text-3xl tracking-[0.1em]">YENİ MERCAN<span className="sr-only"> Restoran Buca Menü</span></h1>
              {table && <span className="mt-2 inline-block rounded-full bg-[#D4AF37] text-[#140D0B] px-3 py-0.5 text-xs font-semibold" data-testid="menu-table-badge">Masa {table}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="sticky top-0 z-20 bg-[#140D0B]/95 backdrop-blur-xl border-b border-[#D4AF37]/15">
        <div className="mx-auto max-w-3xl px-5 pt-4 pb-3 space-y-3">
          <div className="relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#CBBBAE]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Yemek veya malzeme ara…" className="ym-input !pl-11 !rounded-full" data-testid="menu-search-input" />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {data.categories.map((c) => (
              <button key={c.id} onClick={() => jump(c.slug)} className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-colors duration-300 ${activeCat === c.slug ? "bg-[#D4AF37] text-[#140D0B]" : "bg-[#1F1411] text-[#CBBBAE] hover:text-[#FDFBF7]"}`} data-testid={`menu-category-tab-${c.slug}`}>
                {c.name}
              </button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {TAGS.map((t) => (
              <button key={t} onClick={() => setTag(tag === t ? null : t)} className={`whitespace-nowrap rounded-full border px-3 py-1 text-[11px] transition-colors duration-300 ${tag === t ? "border-[#C84B31] bg-[#C84B31] text-white" : "border-[#D4AF37]/25 text-[#E5C158]"}`} data-testid={`menu-tag-filter-${t}`}>
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-5">
        {loading && <p className="py-20 text-center eyebrow" data-testid="menu-loading">Menü yükleniyor…</p>}
        {!loading && !grouped.length && <p className="py-20 text-center text-[#CBBBAE]" data-testid="menu-empty">Aramanıza uygun ürün bulunamadı.</p>}
        {grouped.map((c) => (
          <section key={c.id} id={`cat-${c.slug}`} className="pt-12 scroll-mt-44" data-testid={`menu-category-section-${c.slug}`}>
            <p className="eyebrow">{c.items.length} lezzet</p>
            <h2 className="mt-2 font-serif-ym text-4xl font-semibold">{c.name}</h2>
            {c.description && <p className="mt-2 text-sm text-[#CBBBAE] italic">{c.description}</p>}
            <div className="gold-rule mt-5" />
            {c.items.map((i) => <ItemRow key={i.id} item={i} onOpen={setOpen} />)}
          </section>
        ))}
        {settings.menu_note && (
          <p className="mt-14 flex gap-2 text-xs text-[#CBBBAE] border border-[#D4AF37]/15 rounded-md p-4" data-testid="menu-legal-note">
            <Info size={14} className="text-[#D4AF37] shrink-0 mt-0.5" /> {settings.menu_note}
          </p>
        )}
        <a
          href="https://ektirenproject.com"
          target="_blank"
          rel="noopener noreferrer"
          className={`fixed inset-x-0 z-30 block overflow-hidden py-1.5 bg-[#140D0B]/80 backdrop-blur-md opacity-50 hover:opacity-90 transition-opacity duration-500 ${table ? "bottom-[72px]" : "bottom-0"}`}
          aria-label="Ektiren Project"
          data-testid="menu-agency-credit"
        >
          <div className="marquee-slow flex w-max gap-12 whitespace-nowrap font-display text-[10px] tracking-[0.35em] text-[#CBBBAE]">
            {Array.from({ length: 12 }).map((_, i) => <span key={i}>EKTIREN PROJECT</span>)}
          </div>
        </a>
      </main>
      {table && <TableBar table={table} />}
      <ItemDialog item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
